function removeTokens(url) {
  const urlObj = new URL(url);
  urlObj.searchParams.delete('access_token');
  urlObj.searchParams.delete('refresh_token');
  return urlObj.toString();
}

function extractSectionNames(url) {
  const pathname = new URL(url).pathname;

  const sections = pathname
    .replace(/^\/|\/$/g, "")
    .split("/");

  return sections;
}

// Helper function to extract and format section hierarchy from section URL
function getSectionHierarchy(sectionUrl) {
  if (!sectionUrl) return "NA";

  try {
    const sectionParts = extractSectionNames(sectionUrl);
    // Capitalize first letter of each section and join with comma
    return sectionParts
      .map(section => section.charAt(0).toUpperCase() + section.slice(1))
      .join(",");
  } catch (e) {
    return "NA";
  }
}

function convertTimestampToZTime(timestamp) {
  const date = new Date(timestamp);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(date.getTime() + istOffset);
  return istTime.toISOString().replace('Z', '') + 'Z';
}

export function convertTimestampToYYYYMMDDHHMM(timestamp) {
  if (!timestamp) return "NA";
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // +05:30
  const istDate = new Date(Number(timestamp) + IST_OFFSET_MS);
  const year = istDate.getUTCFullYear();
  const month = String(istDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(istDate.getUTCDate()).padStart(2, "0");
  const hours = String(istDate.getUTCHours()).padStart(2, "0");
  const minutes = String(istDate.getUTCMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export const pushEventToDataLayer = (eventName) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({ event: eventName });
  }
};

export const pushPageViewEvent = (eventName, url, title) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      page: {
        url: url,
        title: title
      }
    });
  }
}

export const addSignUpLoginToDataLayer = (url, data) => {
  if (typeof window !== "undefined" && data) {

    const loginSource = removeTokens(url);
    const userId = data.userData?.id;
    const marketingConsentStatus = data.metadata?.receiveMarketingAndPromitionalEmail || false;
    const newsletterConsentStatus = data.metadata?.dh?.dhNewsLetterTopNewsOfTheDay || false;
    const accountCreationDate = new Date(data.userData?.timeJoined).getTime(); // Get UTC timestamp directly

    const oneMinuteInMillis = 60 * 1000;
    const eventName = Date.now() - accountCreationDate > oneMinuteInMillis ? "login" : "signup";
    const loginMethod = data.metadata?.login_method === "phone_login" ? "mobile"
      : data.metadata?.login_method === "email_login" ? "email"
        : data.metadata?.login_method === "google_login" ? "google" : "unknown";

    window.dataLayer.push({
      event: eventName,
      login_source: loginSource,
      user_id: userId,
      authentication_method: loginMethod,
      marketing_consent_status: marketingConsentStatus,
      newsletter_consent_status: newsletterConsentStatus,
      account_creation_date: convertTimestampToZTime(accountCreationDate), // Use formatted IST time for display only
    });
  }
};

export const addLogoutToDataLayer = (url, data) => {
  if (typeof window !== "undefined" && data) {
    window.dataLayer.push({
      event: "logout",
      login_source: removeTokens(url),
      user_id: data.userData.id,
    });
  }
}

export const addAuthTrackingToDataLayer = (data) => {
  if (typeof window !== "undefined" && data) {
    window.dataLayer.push({
      event: "auth",
      user_id: data.userData.id,
      auth_status: "authenticated"
    });
  }
}

export const addClickTrackingToDataLayer = (url, text, id) => {
  if (typeof window !== "undefined") {
    window.dataLayer.push({
      event: "menuClick",
      click_id: id,
      click_text: text,
      click_url: url,
    });
  }
}

export const addStoryPageViewToDataLayer = (story, commentCount) => {
  if (typeof window !== "undefined" && story) {
    const storyTags = story.tags?.map(tag => tag.name) || [];

    const contentInfo = {
      content_id: story["story-content-id"] || "",
      content_type: "story",
      content_template: story["story-template"] || "",
      public_identifier: story["public-identifier"] || "",
      language: story?.metadata?.language || "english",
    };

    const authorNames = story?.authors?.map(author => author.name) || ["unknown"];

    const storyDetails = {
      page_type: "story",
      story_template: story["story-template"],
      section_name: extractSectionNames(story.sections?.[0]?.["section-url"]).map(
        section => section.charAt(0).toUpperCase() + section.slice(1)
      ) || "unknown",
      author_name: authorNames,
      creator_name: story["owner-name"] || "unknown",
    };

    window.dataLayer.push({
      event: "storyPageView",
      content_info: contentInfo,
      story_details: storyDetails,
      number_comments: commentCount,
      story_tags: storyTags,
      story_words: story["word-count"] || 0,
      published_date: convertTimestampToZTime(story["first-published-at"]) || "",
      last_updated: convertTimestampToZTime(story["last-published-at"]) || "",
    });
  }
};

// Helper function to get comment count from Coral widget
function getCommentCount() {
  if (typeof window !== "undefined") {
    const commentCountElement = document.querySelector(".coral-count");
    if (commentCountElement) {
      const text = commentCountElement.innerText.trim();
      const count = Number(text);
      // Return 0 if count is 0, otherwise return count or "NA" if not a number
      return isNaN(count) ? "NA" : count;
    }
  }
  return "NA";
}

// Unified function to push data layer for all page types
export const pushPageDataLayer = (pageType, userData, pageData = null) => {
  if (typeof window !== "undefined") {
    console.log('🔍 [DataLayer] Received pageType:', pageType);

    // Check login status directly from localStorage (more reliable than Redux)
    // This ensures we always get the correct auth status regardless of Redux timing
    let isLoggedIn = false;
    let userId = null;

    try {
      const userFromLS = localStorage.getItem('user');
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      // User is considered logged in if all three exist
      if (userFromLS && accessToken && refreshToken) {
        const parsedUser = JSON.parse(userFromLS);
        userId = parsedUser?.userData?.id;
        isLoggedIn = !!userId;
      }
    } catch (e) {
      console.error('[Data Layer] Error reading user from localStorage:', e);
    }

    let sanitizedUuid = "NA";
    if (userId) {
      const userIdString = String(userId);
      sanitizedUuid = userIdString.length > 36
        ? userIdString.substring(0, 36)
        : userIdString;
    }

    let dataLayerObject = {
      event: "page_view",
      page_type: "NA",
      author_name: "NA",
      author_id: "NA",
      section_name: "NA",
      published_date: "NA",
      created_date: "NA",
      last_updated_date: "NA",
      content_id: "NA",
      article_id: "NA",
      article_type: "NA",
      story_tags: "NA",
      story_words: "NA",
      creator_name: "NA",
      comment_number: "NA",
      content_title: "NA",
      auth_status: isLoggedIn ? "logged_in" : "non_logged_in",
      uuid: sanitizedUuid,
    };

    if (pageType === "home-page") {
      dataLayerObject.page_type = "home_page";
    } else if (pageType === "story-page" && pageData?.story) {
      const story = pageData.story;

      // Extract author names and IDs
      const authorNames = story.authors?.map(author => author.name).join(",") || "NA";
      const authorIds = story.authors?.map(author => author.id).join(",") || "NA";

      // Extract section name with parent hierarchy (e.g., "India,Andhra Pradesh")
      const sectionName = story.sections?.[0]?.["section-url"]
        ? getSectionHierarchy(story.sections[0]["section-url"])
        : "NA";

      // Extract and format dates
      const publishedDate = convertTimestampToYYYYMMDDHHMM(story["first-published-at"]);
      const lastUpdatedDate = convertTimestampToYYYYMMDDHHMM(story["last-published-at"]);
      const createdDate = convertTimestampToYYYYMMDDHHMM(story["content-created-at"]);

      // Extract tags
      const storyTags = story.tags?.map(tag => tag.name).join(",") || "NA";

      // Update object with story data in consistent order
      dataLayerObject.page_type = "article_page";
      dataLayerObject.author_name = authorNames;
      dataLayerObject.author_id = authorIds;
      dataLayerObject.section_name = sectionName;
      dataLayerObject.published_date = publishedDate;
      dataLayerObject.created_date = createdDate;
      dataLayerObject.last_updated_date = lastUpdatedDate;
      dataLayerObject.content_id = story["story-content-id"] || "NA";
      dataLayerObject.article_id = story["public-identifier"] || "NA";
      dataLayerObject.article_type = story["story-template"] || "NA";
      dataLayerObject.story_tags = storyTags;
      dataLayerObject.story_words = story["word-count"] || "NA";
      dataLayerObject.creator_name = story["owner-name"] || "NA";
      dataLayerObject.comment_number = "NA"; // Will be updated after delay
      dataLayerObject.content_title = story.headline || "NA";

      // For story pages, delay the push to get comment count first
      setTimeout(() => {
        const commentNumber = getCommentCount();
        dataLayerObject.comment_number = commentNumber;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerObject);
        console.log('Story page data layer pushed with comment count:', commentNumber);
      }, 1500);

      // Return early to prevent immediate push below
      return;
    } else if (pageType === "section-page" || pageType === "sub-section-page") {
      // Check if section has a parent-id to determine if it's a sub-section
      const hasParent = pageData?.section?.["parent-id"];

      if (hasParent) {
        dataLayerObject.page_type = "sub_section_page";
      } else {
        dataLayerObject.page_type = "section_page";
      }

      // Extract section name with hierarchy from section URL
      const sectionUrl = pageData?.section?.["section-url"] || pageData?.section?.url;
      if (sectionUrl) {
        dataLayerObject.section_name = getSectionHierarchy(sectionUrl);
      }
    } else if (pageType === "tag-page" && pageData) {
      // Tag page specific data
      const tagName = pageData.tags?.name || pageData.tagName || "NA";

      dataLayerObject.page_type = "tag_page";
      dataLayerObject.story_tags = tagName;
    } else if (pageType === "author-page" && pageData?.author) {
      // Author page specific data
      const authorName = pageData.author.name || "NA";
      const authorId = pageData.author.id || "NA";

      dataLayerObject.page_type = "author_page";
      dataLayerObject.author_name = authorName;
      dataLayerObject.author_id = authorId;
    } else if (pageType === "collection-of-collection-page" && pageData?.collection) {
      // Collection page specific data
      dataLayerObject.page_type = "collection_page";
    } else if (pageType === "search-page") {
      // Search page specific data
      dataLayerObject.page_type = "search_page";
    } else if (pageType === "not-found-page" || pageType === "404") {
      // 404 page specific data
      dataLayerObject.page_type = "404_page";
    } else {
      // For other page types, just set the page_type
      dataLayerObject.page_type = pageType || "NA";
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerObject);
  }
};

